import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Check,
  X,
  Crown,
  Shield,
  Brain,
  Globe,
  Layers,
  Bot,
  Workflow,
  MessageSquare,
  Cpu,
  ArrowLeft,
  Eye,
  Sparkles,
} from 'lucide-react';
import Navbar from '../src/components/landing/Navbar';
import Footer from '../src/components/landing/Footer';

import '../pages/landing.module.css';

/* ── data ──────────────────────────────────────────────────────────── */

type CellValue = 'yes' | 'no' | 'partial' | string;

interface CompareRow {
  feature: string;
  tasking: CellValue;
  nanoclaw: CellValue;
  openclaw: CellValue;
}

const categories: { title: string; icon: React.ReactNode; rows: CompareRow[] }[] = [
  {
    title: 'Browser Automation',
    icon: <Globe size={20} />,
    rows: [
      { feature: 'Cloud browser agent', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'Multi-strategy click fallback (text match, JS click, href follow, Playwright locator, getByText)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Page state extraction (forms, clickable elements, visible text)', tasking: 'yes', nanoclaw: 'partial', openclaw: 'partial' },
      { feature: 'Accessibility tree snapshots', tasking: 'yes', nanoclaw: 'yes', openclaw: 'partial' },
      { feature: 'Real-time WebSocket browser streaming', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Multi-tab browser support', tasking: 'yes', nanoclaw: 'no', openclaw: 'partial' },
      { feature: 'Credential auto-injection into browser sessions', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'TaskTaker meeting bot (join Zoom, Meet, Teams, WebEx, Jitsi)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
    ],
  },
  {
    title: 'Task Management & Autonomy',
    icon: <Workflow size={20} />,
    rows: [
      { feature: 'Task dependency chains (depends_on)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Postcondition verification (done_when)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Subtask decomposition', tasking: 'yes', nanoclaw: 'no', openclaw: 'partial' },
      { feature: 'Scheduled tasks / autonomous execution', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'Visual task board (Kanban)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Workflow planner before execution', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Pre-execution reasoning protocol (5-step analysis)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
    ],
  },
  {
    title: 'Security & Isolation',
    icon: <Shield size={20} />,
    rows: [
      { feature: 'Container-isolated workloads with browser sandboxing', tasking: 'yes', nanoclaw: 'yes', openclaw: 'no' },
      { feature: 'Chromium sandbox enabled for browser engine', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Non-root execution for agents', tasking: 'yes', nanoclaw: 'yes', openclaw: 'partial' },
      { feature: 'Encrypted credential storage', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'Edge WAF and DDoS protection by default', tasking: 'yes', nanoclaw: 'partial', openclaw: 'partial' },
      { feature: 'Safe for typical use without additional hardening', tasking: 'yes', nanoclaw: 'partial', openclaw: 'no' },
      { feature: 'Auto-scaling to zero for idle workloads', tasking: 'yes', nanoclaw: 'partial', openclaw: 'partial' },
    ],
  },
  {
    title: 'Agent Modes',
    icon: <Bot size={20} />,
    rows: [
      { feature: 'Cloud browser agent', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'Chrome extension passthrough', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Desktop agent', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Per-task agent mode selection', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
    ],
  },
  {
    title: 'Integrations & Channels',
    icon: <MessageSquare size={20} />,
    rows: [
      { feature: 'Web app / dashboard', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'WhatsApp', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'Telegram', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'Slack', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'Discord', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'Signal', tasking: 'yes', nanoclaw: 'yes', openclaw: 'partial' },
      { feature: 'Chrome extension', tasking: 'yes', nanoclaw: 'partial', openclaw: 'partial' },
      { feature: 'Voice / calling interface', tasking: 'yes', nanoclaw: 'partial', openclaw: 'partial' },
      { feature: 'Broad SaaS integration catalog (50+ per vendor docs)', tasking: 'partial', nanoclaw: 'partial', openclaw: 'yes' },
    ],
  },
  {
    title: 'AI & Models',
    icon: <Brain size={20} />,
    rows: [
      { feature: 'Claude (Anthropic)', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'GPT-4o / OpenAI', tasking: 'yes', nanoclaw: 'partial', openclaw: 'yes' },
      { feature: 'Gemini / Google', tasking: 'yes', nanoclaw: 'no', openclaw: 'yes' },
      { feature: 'Local / self-hosted models', tasking: 'yes', nanoclaw: 'no', openclaw: 'yes' },
      { feature: 'Per-task model selection', tasking: 'yes', nanoclaw: 'no', openclaw: 'partial' },
    ],
  },
  {
    title: 'Skills & Ecosystem',
    icon: <Layers size={20} />,
    rows: [
      { feature: 'Skills marketplace with search & discovery', tasking: 'yes', nanoclaw: 'no', openclaw: 'yes' },
      { feature: 'Community skills (tippyentertainment, ClawdHub)', tasking: 'yes', nanoclaw: 'no', openclaw: 'yes' },
      { feature: 'Custom skill authoring', tasking: 'yes', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'Image generation / processing', tasking: 'yes', nanoclaw: 'no', openclaw: 'yes' },
      { feature: '3D model generation', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
    ],
  },
  {
    title: 'Auditability & Governance',
    icon: <Eye size={20} />,
    rows: [
      { feature: 'Per-task execution logs', tasking: 'yes', nanoclaw: 'no', openclaw: 'partial' },
      { feature: 'Policy-gated approval workflows', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Credential governance (encrypted, never exposed in chat)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Session-level audit trail', tasking: 'yes', nanoclaw: 'partial', openclaw: 'partial' },
    ],
  },
  {
    title: 'App Building & Hosting',
    icon: <Sparkles size={20} />,
    rows: [
      { feature: 'In-platform app/site builder', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Live app hosting & publishing', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Vibe coding sessions (AI builds the app)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Real-time sandboxed code execution', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Custom domain support', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
    ],
  },
  {
    title: 'Infrastructure & Ops',
    icon: <Cpu size={20} />,
    rows: [
      { feature: 'Cloud-native deployment', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Auto-scaling (up and down to zero)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Edge-distributed on Cloudflare Workers (300+ locations)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Self-hostable', tasking: 'partial', nanoclaw: 'yes', openclaw: 'yes' },
      { feature: 'Zero-ops (fully managed)', tasking: 'yes', nanoclaw: 'no', openclaw: 'no' },
      { feature: 'Persistent storage (KV + D1 database)', tasking: 'yes', nanoclaw: 'partial', openclaw: 'partial' },
    ],
  },
];

const verdictRows: { category: string; winner: 'tasking' | 'nanoclaw' | 'openclaw' | 'tie'; reason: string }[] = [
  { category: 'Browser Automation', winner: 'tasking', reason: 'Multi-strategy click fallback, page extraction, WebSocket streaming, credential injection, TaskTaker meeting bot — all three have browser agents, but Tasking.tech goes deeper' },
  { category: 'Task Management & Autonomy', winner: 'tasking', reason: 'Dependencies, subtasks, postcondition verification, visual board, pre-execution reasoning — purpose-built task system' },
  { category: 'Security & Isolation', winner: 'tasking', reason: 'Container isolation (shared with NanoClaw) + Chromium sandbox + edge WAF + auto-scale to zero — NanoClaw is strong here too' },
  { category: 'Agent Modes', winner: 'tasking', reason: 'Cloud browser + Chrome extension + desktop agent, all selectable per task' },
  { category: 'Integrations & Channels', winner: 'tie', reason: 'All three support major messaging channels — OpenClaw is positioned as having the broadest SaaS catalog, Tasking.tech leads in Chrome extension and voice' },
  { category: 'AI & Models', winner: 'tie', reason: 'Tasking.tech and OpenClaw both advertise support for Claude, GPT-4o, Gemini, and local models — Tasking.tech adds per-task model selection; verify exact models in each vendor\'s current docs' },
  { category: 'Skills & Ecosystem', winner: 'tie', reason: 'Both have skills marketplaces — Tasking.tech highlights 3D generation and media capabilities, OpenClaw highlights broader community-driven skills' },
  { category: 'Auditability & Governance', winner: 'tasking', reason: 'Per-task logs, policy-gated approvals, credential governance, session audit trails' },
  { category: 'App Building & Hosting', winner: 'tasking', reason: 'In-platform app builder with vibe coding, live hosting, sandboxed execution, custom domains — unique to Tasking.tech' },
  { category: 'Infrastructure & Ops', winner: 'tasking', reason: 'Cloud-native on Cloudflare Workers (300+ edge locations), auto-scaling to zero, zero-ops — NanoClaw and OpenClaw are self-hosted' },
];

/* ── helpers ───────────────────────────────────────────────────────── */

function CellIcon({ value }: { value: CellValue }) {
  if (value === 'yes') return <Check size={18} className="text-green-500" />;
  if (value === 'no') return <X size={18} className="text-red-400/60" />;
  if (value === 'partial')
    return <span className="text-xs font-medium text-yellow-500">Partial</span>;
  return <span className="text-xs text-muted-foreground">{value}</span>;
}

function WinnerBadge({ who }: { who: string }) {
  const colors: Record<string, string> = {
    tasking: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    nanoclaw: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    openclaw: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    tie: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  const labels: Record<string, string> = {
    tasking: 'Tasking.tech',
    nanoclaw: 'NanoClaw',
    openclaw: 'OpenClaw',
    tie: 'Tie',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${colors[who]}`}>
      {who === 'tasking' && <Crown size={12} />}
      {labels[who]}
    </span>
  );
}

/* ── page ──────────────────────────────────────────────────────────── */

const ComparePage = () => {
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
      document.body.style.overflow = 'auto';
    }
    return () => {
      if (root) {
        root.style.overflow = '';
        root.style.height = '';
        document.body.style.overflow = '';
      }
    };
  }, []);

  const totalCategories = verdictRows.length;
  const taskingWins = verdictRows.filter((r) => r.winner === 'tasking').length;

  return (
    <div className="min-h-screen bg-background text-foreground overflow-auto">
      <>
        <title>How We Compare | Tasking.tech vs NanoClaw vs OpenClaw</title>
        <meta
          name="description"
          content="See how Tasking.tech compares to NanoClaw and OpenClaw across browser automation, task management, security, integrations, AI models, and more."
        />
        <meta property="og:title" content="How We Compare | Tasking.tech vs NanoClaw vs OpenClaw" />
        <meta
          property="og:description"
          content="Feature-by-feature comparison of the leading AI agent platforms."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tasking.tech/compare" />
        <link rel="canonical" href="https://tasking.tech/compare" />
      </>

      <Navbar />

      {/* Hero */}
      <section className="section-padding pt-32">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft size={14} /> Back to home
            </Link>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              How do we <span className="text-gradient">compare</span>?
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
              Feature-by-feature comparison of Tasking.tech, NanoClaw, and OpenClaw across {totalCategories} categories.
            </p>
          </motion.div>

          {/* Legend */}
          <div className="mt-10 glass-card rounded-xl px-6 py-3 inline-flex items-center gap-8 text-sm text-muted-foreground mx-auto" style={{ display: 'flex', justifyContent: 'center' }}>
            <span className="flex items-center gap-1.5"><Check size={16} className="text-green-500" /> Supported</span>
            <span className="flex items-center gap-1.5"><X size={16} className="text-red-400/60" /> Not supported</span>
            <span className="flex items-center gap-1.5"><span className="text-xs font-medium text-yellow-500">Partial</span> Limited support</span>
          </div>
        </div>
      </section>

      {/* Comparison tables */}
      <section className="pb-16">
        <div className="mx-auto max-w-7xl px-6 space-y-12">
          {categories.map((cat, ci) => (
            <motion.div
              key={ci}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: ci * 0.05 }}
            >
              <div className="flex items-center gap-2.5 mb-4">
                <span className="text-orange-500">{cat.icon}</span>
                <h2 className="text-xl font-semibold text-foreground">{cat.title}</h2>
              </div>
              <div className="glass-card rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left px-5 py-3.5 text-muted-foreground font-medium w-[40%]">Feature</th>
                      <th className="text-center px-4 py-3.5 font-semibold text-orange-400 w-[20%]">
                        Tasking.tech
                      </th>
                      <th className="text-center px-4 py-3.5 font-medium text-muted-foreground w-[20%]">NanoClaw</th>
                      <th className="text-center px-4 py-3.5 font-medium text-muted-foreground w-[20%]">OpenClaw</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.rows.map((row, ri) => (
                      <tr
                        key={ri}
                        className={`border-b border-border/20 ${ri % 2 === 0 ? '' : 'bg-white/[0.02]'}`}
                      >
                        <td className="px-5 py-3 text-foreground/90">{row.feature}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center"><CellIcon value={row.tasking} /></div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center"><CellIcon value={row.nanoclaw} /></div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center"><CellIcon value={row.openclaw} /></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          ))}

          {/* Verdict */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-2.5 mb-4">
              <Crown size={20} className="text-orange-500" />
              <h2 className="text-xl font-semibold text-foreground">The Verdict</h2>
            </div>
            <div className="glass-card rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left px-5 py-3.5 text-muted-foreground font-medium w-[25%]">Category</th>
                    <th className="text-left px-4 py-3.5 text-muted-foreground font-medium w-[15%]">Winner</th>
                    <th className="text-left px-4 py-3.5 text-muted-foreground font-medium w-[60%]">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {verdictRows.map((row, ri) => (
                    <tr
                      key={ri}
                      className={`border-b border-border/20 ${ri % 2 === 0 ? '' : 'bg-white/[0.02]'}`}
                    >
                      <td className="px-5 py-3 font-medium text-foreground/90">{row.category}</td>
                      <td className="px-4 py-3"><WinnerBadge who={row.winner} /></td>
                      <td className="px-4 py-3 text-muted-foreground">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center pt-8"
          >
            <p className="text-2xl font-bold text-foreground mb-2">
              Tasking.tech leads in <span className="text-gradient">{taskingWins} of {totalCategories}</span> categories.
            </p>
            <p className="text-muted-foreground mb-4 max-w-2xl mx-auto">
              An AI agent platform that combines full task autonomy, sandboxed browser isolation,
              multi-agent modes, in-platform app building, and a skills marketplace — all cloud-native, zero-ops.
            </p>
            <p className="text-xs text-muted-foreground/50 mb-8">
              This is a vendor-produced comparison based on publicly available documentation as of March 2026.
              Features and capabilities change over time — verify claims in each vendor's current docs.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link
                to="/login"
                className="rounded-lg bg-primary px-8 py-3 text-lg font-semibold text-primary-foreground transition-opacity hover:opacity-90"
              >
                Get Started Free
              </Link>
              <Link
                to="/pricing"
                className="rounded-lg border border-border bg-secondary px-8 py-3 text-lg font-semibold text-foreground transition-colors hover:border-primary/30"
              >
                View Pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ComparePage;
